import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import type { SubscriptionPlan } from '@shared/types';

export function PricingPage() {
  const { data: plans, isLoading } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: () => api<SubscriptionPlan[]>('/api/subscription-plans'),
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Pricing Plans
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the right plan to list your farm packages and harvests on our marketplace. 
            All plans include secure payment processing and buyer inquiry management.
          </p>
        </div>

        {/* Plans Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading plans...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans?.map((plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))}
          </div>
        )}

        {/* FAQ/CTA Section */}
        <div className="mt-16 text-center">
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle>Need a custom plan?</CardTitle>
              <CardDescription>
                Contact us for enterprise pricing or custom features for large-scale farming operations.
              </CardDescription>
            </CardHeader>
            <CardFooter className="justify-center">
              <Button variant="outline">Contact Sales</Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
}

interface PlanCardProps {
  plan: SubscriptionPlan;
}

function PlanCard({ plan }: PlanCardProps) {
  const isPopular = plan.slug === 'professional'; // Highlight professional plan

  return (
    <Card className={isPopular ? 'border-primary shadow-lg' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between mb-2">
          <CardTitle>{plan.name}</CardTitle>
          {isPopular && <Badge variant="default">Popular</Badge>}
          {plan.status === 'archived' && <Badge variant="secondary">Archived</Badge>}
        </div>
        <div className="mt-4">
          <span className="text-4xl font-bold">
            {plan.currency === 'GHS' ? '₵' : plan.currency} {plan.price.toLocaleString()}
          </span>
          <span className="text-muted-foreground">
            /{plan.billingCycle === 'monthly' ? 'month' : 'year'}
          </span>
        </div>
        <CardDescription className="mt-2">
          Perfect for {plan.slug === 'basic' ? 'individual farmers' : plan.slug === 'professional' ? 'growing businesses' : 'enterprises'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <FeatureRow
            included={true}
            text={`${plan.features.listingLimit === -1 ? 'Unlimited' : plan.features.listingLimit} active listings`}
          />
          <FeatureRow
            included={plan.features.priorityPlacement}
            text="Priority search placement"
          />
          <FeatureRow
            included={plan.features.verifiedBadge}
            text="Verified seller badge"
          />
          <FeatureRow
            included={plan.features.analytics}
            text="Advanced analytics dashboard"
          />
          <FeatureRow
            included={plan.features.bulkUpload}
            text="Bulk listing upload"
          />
          {plan.features.featuredListings > 0 && (
            <FeatureRow
              included={true}
              text={`${plan.features.featuredListings} featured listings/month`}
            />
          )}
          <FeatureRow
            included={plan.features.apiAccess}
            text="API access"
          />
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          className="w-full" 
          variant={isPopular ? 'default' : 'outline'}
          disabled={plan.status === 'archived'}
        >
          {plan.status === 'archived' ? 'Not Available' : 'Get Started'}
        </Button>
      </CardFooter>
    </Card>
  );
}

interface FeatureRowProps {
  included: boolean;
  text: string;
}

function FeatureRow({ included, text }: FeatureRowProps) {
  return (
    <div className="flex items-center gap-2">
      {included ? (
        <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
      ) : (
        <X className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      )}
      <span className={included ? 'text-foreground' : 'text-muted-foreground line-through'}>
        {text}
      </span>
    </div>
  );
}
